/**
 * AdminAnnouncementComposer — modal that lets an admin post a new
 * announcement. Used from the AdminDashboard "Post announcement" button.
 */
import React, { useEffect, useState } from 'react';
import { X, Megaphone, Loader2, Trash2, RotateCw, Users } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TONES = [
  { id: 'info',       label: 'Info' },
  { id: 'success',    label: 'Success' },
  { id: 'warn',       label: 'Warning' },
  { id: 'celebrate',  label: 'Celebrate' },
];

const AUDIENCES = [
  { id: 'all',     label: 'Everyone',     hint: 'Users + Writers + Admins' },
  { id: 'users',   label: 'Users only',   hint: 'Readers' },
  { id: 'writers', label: 'Writers only', hint: 'Authors + creators' },
  { id: 'admins',  label: 'Admins only',  hint: 'Admins + co-admins' },
];

const AUDIENCE_BTN_COPY = {
  all:     'Broadcast to everyone',
  users:   'Broadcast to users only',
  writers: 'Broadcast to scribes only',
  admins:  'Broadcast to admins only',
};

const AdminAnnouncementComposer = ({ open, onClose }) => {
  const [form, setForm] = useState({ title: '', body: '', tone: 'info', audience: 'all', cta_label: '', cta_url: '' });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/announcements`);
      if (r.ok) {
        const d = await r.json();
        setHistory(d.announcements || []);
      }
    } catch { /* ignore */ }
    setLoadingHistory(false);
  };

  useEffect(() => {
    if (open) fetchHistory();
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title + body required');
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('✓ Announcement broadcast');
      setForm({ title: '', body: '', tone: 'info', audience: 'all', cta_label: '', cta_url: '' });
      fetchHistory();
    } catch (err) {
      toast.error(`Failed: ${err.message || err}`);
    }
    setBusy(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement permanently?')) return;
    try {
      await authFetch(`${BACKEND_URL}/api/admin/announcements/${id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchHistory();
    } catch { /* ignore */ }
  };
  const handleRepublish = async (id) => {
    try {
      await authFetch(`${BACKEND_URL}/api/admin/announcements/${id}/republish`, { method: 'POST' });
      toast.success('Republished — readers will see it again');
      fetchHistory();
    } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ann-composer-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border border-indigo-500/40 rounded-2xl shadow-2xl"
        data-testid="ann-composer-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-fuchsia-300" />
            Broadcast announcement
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-white"
            data-testid="ann-composer-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Title (e.g. 🎉 New feature live)"
            maxLength={120}
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-indigo-400 focus:outline-none"
            data-testid="ann-title"
          />
          <textarea
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="What's new? (markdown-lite supported in the future)"
            rows={4}
            maxLength={1000}
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-indigo-400 focus:outline-none resize-none"
            data-testid="ann-body"
          />
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm((f) => ({ ...f, tone: t.id }))}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  form.tone === t.id
                    ? 'bg-indigo-500/30 border-indigo-300 text-white'
                    : 'bg-slate-800 border-slate-700 text-gray-400 hover:border-slate-500'
                }`}
                data-testid={`ann-tone-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Audience picker — dropdown so it works cleanly on small
              touch targets and stays out of the way until tapped.
              Default "Everyone" keeps the original behaviour. */}
          <div>
            <label htmlFor="ann-audience" className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-1">
              <Users className="w-3 h-3 text-indigo-300" />
              Audience
            </label>
            <select
              id="ann-audience"
              value={form.audience}
              onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-indigo-400 focus:outline-none appearance-none cursor-pointer"
              data-testid="ann-audience"
            >
              {AUDIENCES.map((a) => (
                <option key={a.id} value={a.id} className="bg-slate-900">
                  {a.label} — {a.hint}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={form.cta_label}
              onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
              placeholder="CTA label (optional, e.g. Read more)"
              maxLength={40}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none"
              data-testid="ann-cta-label"
            />
            <input
              type="text"
              value={form.cta_url}
              onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))}
              placeholder="CTA URL (e.g. /book-club)"
              maxLength={400}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none"
              data-testid="ann-cta-url"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-600 hover:from-indigo-400 hover:to-fuchsia-500 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="ann-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            {AUDIENCE_BTN_COPY[form.audience] || 'Broadcast'}
          </button>
        </form>

        <div className="border-t border-slate-700 p-4">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-2">Recent broadcasts</p>
          {loadingHistory ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-500">No announcements yet.</p>
          ) : (
            <ul className="space-y-2">
              {history.slice(0, 8).map((a) => (
                <li
                  key={a.announcement_id}
                  className="flex items-start gap-2 p-2 rounded-md bg-slate-800/50 border border-slate-700"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{a.title}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {new Date(a.created_at).toLocaleString()} · {a.tone} ·{' '}
                      {a.dismissal_count} dismissals
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRepublish(a.announcement_id)}
                    className="w-7 h-7 rounded-md hover:bg-emerald-500/20 text-emerald-300 flex items-center justify-center"
                    title="Republish (reset dismissals)"
                    data-testid={`ann-republish-${a.announcement_id}`}
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(a.announcement_id)}
                    className="w-7 h-7 rounded-md hover:bg-red-500/20 text-red-300 flex items-center justify-center"
                    title="Delete"
                    data-testid={`ann-delete-${a.announcement_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncementComposer;
